import { useRouter } from "next/router";
import { BreadCrumb } from "primereact/breadcrumb";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageType } from "../../../../common/components";
import { Card } from "../../../../common/components/Card";
import {
    DataTableSettings,
    DataTableState,
} from "../../../../common/models/table.model";
import { showToast } from "../../../../common/services/toast";
import {
    TABLE_IDS,
    useTableStore,
} from "../../../../common/store/useTableStore";
import {
    useGetAnnouncementFilters,
    useLiveAnnouncementsStream,
    usePostAnnouncementRequest,
} from "../../api";
import { ANNOUNCEMENT_TABLE_SORT_TYPES } from "../../constants";
import { Announcement } from "../../models";
import { AnnouncementsTable } from "../AnnouncementsTable";

const initAnnouncementsRequest: DataTableState<Announcement> = {
    items: [],
    itemCount: 0,
    pageCount: 0,
    request: {
        page: 1,
        pageSize: 25,
        search: null,
        sortAsc: false,
        sortType: ANNOUNCEMENT_TABLE_SORT_TYPES.START_DATE_TIME,
        filters: [],
        dateFrom: null,
        dateTo: null,
    },
};

const AnnouncementsContainer = () => {
    const { t, i18n } = useTranslation(["common", "announcements", "toast"]);

    const router = useRouter();

    const [tableState, setTableState] = useState<DataTableState<Announcement>>(
        initAnnouncementsRequest
    );

    const { filters, setFilters } = useTableStore(TABLE_IDS.ANNOUNCEMENTS);

    const {
        announcementFilters,
        triggerGetAnnouncementFilters,
        isLoadingAnnouncementFilters,
    } = useGetAnnouncementFilters();

    const {
        triggerPostAnnouncementRequest,
        isLoadingPostAnnouncementRequest,
        resetPostAnnouncementRequest,
    } = usePostAnnouncementRequest({
        onSuccess: (newData: DataTableState<Announcement>) => {
            setTableState(newData);
            setFilters(newData.request.filters ?? []);
        },
        onError: () => {
            resetPostAnnouncementRequest();
            showToast({
                summary: t("toast:announcements.loadFailed.title"),
                detail: t("toast:announcements.loadFailed.description"),
                severity: MessageType.ERROR,
            });
        },
    });

    const triggerRequestAnnouncements = (arg: DataTableState<Announcement>) =>
        triggerPostAnnouncementRequest({
            body: arg.request,
            locale: router.locale,
        });

    const handleRefreshAnnouncements = () =>
        triggerRequestAnnouncements(tableState);

    useLiveAnnouncementsStream({
        onMessage: (payload) => {
            if (!payload) return;
            setTableState((prev) => ({
                ...prev,
                items: prev.items.map((announcement) =>
                    announcement.id === payload.id ? payload : announcement
                ),
            }));
        },
    });

    const getAnnouncementsRequestData = (
        getNewSettings: (oldState: DataTableSettings) => DataTableSettings
    ) => {
        triggerRequestAnnouncements({
            ...tableState,
            request: {
                ...settings,
                ...getNewSettings(settings),
            },
        });
    };

    useEffect(() => {
        if (router.isReady && i18n.language) {
            triggerRequestAnnouncements({
                ...tableState,
                request: {
                    ...tableState.request,
                    filters: filters ?? null,
                },
            });
            triggerGetAnnouncementFilters({ locale: router.locale });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady, i18n.language]);

    const {
        items: announcements,
        request: settings,
        ...tableProps
    } = tableState;

    return (
        <div className="px-8 pb-8 flex flex-col h-full">
            <BreadCrumb
                className="border-none bg-inherit"
                model={[{ label: t("announcements:announcements") ?? "" }]}
                home={{ icon: "pi pi-home" }}
                pt={{
                    menuitem: { className: "mx-2" },
                    action: {
                        className: "!shadow-none cursor-default",
                    },
                }}
            />
            <Card
                title={t("common:overview")}
                className="flex-1 flex flex-col min-h-0"
                pt={{
                    title: { className: "mb-4" },
                    body: { className: "h-full" },
                    content: { className: "h-[calc(100%-4rem)] py-0" },
                }}
            >
                <AnnouncementsTable
                    id={TABLE_IDS.ANNOUNCEMENTS}
                    announcements={announcements}
                    settings={settings}
                    tableProps={tableProps}
                    getAnnouncementsRequestData={getAnnouncementsRequestData}
                    isLoadingAnnouncements={isLoadingPostAnnouncementRequest}
                    locale={i18n.language}
                    filterConfigurations={announcementFilters}
                    isLoadingFilterConfig={isLoadingAnnouncementFilters}
                    onCreateSuccess={handleRefreshAnnouncements}
                    onEditSuccess={handleRefreshAnnouncements}
                    onDeleteSuccess={handleRefreshAnnouncements}
                />
            </Card>
        </div>
    );
};

export default AnnouncementsContainer;
